import React, { useState, useCallback } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Button, Box, Paper,
  TextField, Autocomplete, Switch, FormControlLabel, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Menu, MenuItem, ListItemIcon,
} from '@mui/material';
import {
  Assessment, FileDownload, Search, BarChart as BarChartIcon,
  CalendarMonth, TrendingUp, AccountBalance, Receipt as InvoiceIcon,
  PictureAsPdf, TableChart, Description as CsvIcon, ArrowDropDown,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { format, subDays, startOfMonth } from 'date-fns';
import { useProjects } from '../context_providers/ProjectContext';
import { useOrganization } from '../context_providers/OrganizationContext';
import { useNotification } from '../context_providers/NotificationContext';
import { ReportResult, User } from '../../types';
import apiService from '../../utils/api.service';
import LoadingSpinner from '../shared/LoadingSpinner';


// ==================== Constants ====================
const CHART_COLORS = ['#7b6df6', '#10b981', '#3b82f6', '#f59e0b', '#f43f5e', '#06b6d4', '#f97316', '#8b5cf6'];

type ReportTab = 'summary' | 'by-project' | 'by-user' | 'by-client' | 'by-date' | 'utilization' | 'budget';

interface ReportFilters {
  startDate: string;
  endDate: string;
  projectIds: string[];
  clientIds: string[];
  userIds: string[];
  billableOnly: boolean;
}

interface GroupedResult {
  name: string;
  id?: string;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  entryCount: number;
  percentOfTotal: number;
}

interface DateResult {
  date: string;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  entryCount: number;
}

interface InvoicePreview {
  invoiceNumber: string;
  date: string;
  from: { name: string };
  to: { name?: string; contactName?: string; contactEmail?: string; address?: string };
  project: string;
  period: { start: string; end: string };
  lineItems: { date: string; user: string; task: string; description: string; hours: number; rate: number; amount: number }[];
  totalHours: number;
  totalAmount: number;
}

// ==================== Custom Tooltip ====================
const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 1.5, px: 2, py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
      {payload.map((p: any, i: number) => (
        <Typography key={i} variant="body2" fontWeight={600} sx={{ color: p.color || p.fill }}>
          {p.name}: {p.value?.toFixed(1)}h
        </Typography>
      ))}
    </Box>
  );
};

// ==================== Stat Card ====================
const StatCard: React.FC<{ label: string; value: string | number; color: string; suffix?: string }> = ({
  label, value, color, suffix = '',
}) => (
  <Card sx={{
    background: `linear-gradient(135deg, ${color}20, ${color}08)`,
    border: '1px solid rgba(148, 163, 184, 0.08)',
  }}>
    <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
      <Typography variant="h4" fontWeight={700}>{value}{suffix}</Typography>
    </CardContent>
  </Card>
);

// ==================== Main Component ====================
const ReportsPage: React.FC = () => {
  const { projects, clients } = useProjects();
  const { organization } = useOrganization();
  const { showError, showSuccess } = useNotification();

  // Filters
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    projectIds: [],
    clientIds: [],
    userIds: [],
    billableOnly: false,
  });

  // State
  const [activeTab, setActiveTab] = useState<ReportTab>('summary');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Results
  const [summary, setSummary] = useState<ReportResult | null>(null);
  const [groupedData, setGroupedData] = useState<GroupedResult[]>([]);
  const [dateData, setDateData] = useState<DateResult[]>([]);
  const [utilizationData, setUtilizationData] = useState<GroupedResult[]>([]);
  const [budgetData, setBudgetData] = useState<any[]>([]);

  // Members for user filter
  const [members, setMembers] = useState<User[]>([]);
  const [membersLoaded, setMembersLoaded] = useState(false);

  // Export menu
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  // Invoice dialog
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceProjectId, setInvoiceProjectId] = useState<string>('');
  const [invoicePreview, setInvoicePreview] = useState<InvoicePreview | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);


  const loadMembers = useCallback(async () => {
    if (membersLoaded) return;
    try {
      const response = await apiService.listOrgMembers();
      const raw = response.data || response;
      setMembers(Array.isArray(raw) ? raw : (raw?.members || []));
      setMembersLoaded(true);
    } catch {
      // Non-critical
    }
  }, [membersLoaded]);

  const buildApiParams = (): Record<string, string> => {
    const params: Record<string, string> = {};
    if (filters.startDate) params.start_date = filters.startDate;
    if (filters.endDate) params.end_date = filters.endDate;
    if (filters.projectIds.length) params.project_id = filters.projectIds.join(',');
    if (filters.clientIds.length) params.client_id = filters.clientIds.join(',');
    if (filters.userIds.length) params.user_id = filters.userIds.join(',');
    if (filters.billableOnly) params.billable_only = 'true';
    return params;
  };

  const normalizeGrouped = (raw: any[], nameKey: string): GroupedResult[] => {
    const items = Array.isArray(raw) ? raw : [];
    const grandTotal = items.reduce((s, it) => {
      const hours = it.totalHours || it.hours || (it.totalMinutes ? it.totalMinutes / 60 : 0);
      return s + hours;
    }, 0);
    return items.map((it) => {
      const totalHours = it.totalHours || it.hours || (it.totalMinutes ? it.totalMinutes / 60 : 0);
      const billableHours = it.billableHours || (it.billableMinutes ? it.billableMinutes / 60 : 0);
      return {
        name: it[nameKey] || it.name || 'Unknown',
        id: it.id || it.projectId || it.userId || it.clientId,
        totalHours: Math.round(totalHours * 10) / 10,
        billableHours: Math.round(billableHours * 10) / 10,
        nonBillableHours: Math.round((totalHours - billableHours) * 10) / 10,
        entryCount: it.entryCount || it.count || 0,
        percentOfTotal: grandTotal > 0 ? (totalHours / grandTotal) * 100 : 0,
      };
    }).sort((a, b) => b.totalHours - a.totalHours);
  };

  const normalizeDateData = (raw: any[]): DateResult[] => {
    const items = Array.isArray(raw) ? raw : [];
    return items.map((it) => {
      const totalHours = it.totalHours || it.hours || (it.totalMinutes ? it.totalMinutes / 60 : 0);
      const billableHours = it.billableHours || (it.billableMinutes ? it.billableMinutes / 60 : 0);
      return {
        date: it.date || '',
        totalHours: Math.round(totalHours * 10) / 10,
        billableHours: Math.round(billableHours * 10) / 10,
        nonBillableHours: Math.round((totalHours - billableHours) * 10) / 10,
        entryCount: it.entryCount || it.count || 0,
      };
    }).sort((a, b) => a.date.localeCompare(b.date));
  };

  const generateReport = async () => {
    setIsLoading(true);
    setHasGenerated(true);
    const params = buildApiParams();

    try {
      switch (activeTab) {
        case 'summary': {
          const resp = await apiService.getReportSummary(params);
          const data = resp.data || resp;
          setSummary({
            totalHours: data.totalHours || 0,
            billableHours: data.billableHours || 0,
            nonBillableHours: data.nonBillableHours || 0,
            entryCount: data.entryCount || 0,
            billablePercent: data.billablePercent || 0,
          });
          break;
        }
        case 'by-project': {
          const resp = await apiService.getReportByProject(params);
          const raw = resp.data || resp;
          setGroupedData(normalizeGrouped(raw?.projects || raw || [], 'projectName'));
          break;
        }
        case 'by-user': {
          const resp = await apiService.getReportByUser(params);
          const raw = resp.data || resp;
          setGroupedData(normalizeGrouped(raw?.users || raw || [], 'userName'));
          break;
        }
        case 'by-client': {
          const resp = await apiService.getReportByClient(params);
          const raw = resp.data || resp;
          setGroupedData(normalizeGrouped(raw?.clients || raw || [], 'clientName'));
          break;
        }
        case 'by-date': {
          const resp = await apiService.getReportByDate(params);
          const raw = resp.data || resp;
          setDateData(normalizeDateData(raw?.dates || raw || []));
          break;
        }
        case 'utilization': {
          const resp = await apiService.getUtilizationReport(params);
          const raw = resp.data || resp;
          setUtilizationData(normalizeGrouped(raw?.utilization || raw?.users || raw || [], 'userName'));
          break;
        }
        case 'budget': {
          const resp = await apiService.getBudgetReport(params);
          const raw = resp.data || resp;
          const items = Array.isArray(raw) ? raw : (raw?.budgets || raw?.projects || []);
          setBudgetData(items.map((it: any) => ({
            name: it.projectName || it.name || 'Unknown',
            budgetHours: it.budgetHours || 0,
            actualHours: it.actualHours || it.usedHours || it.totalHours || 0,
            remainingHours: it.remainingHours || Math.max(0, (it.budgetHours || 0) - (it.actualHours || it.usedHours || it.totalHours || 0)),
            percentUsed: it.percentUsed || (it.budgetHours > 0 ? ((it.actualHours || it.usedHours || it.totalHours || 0) / it.budgetHours) * 100 : 0),
          })));
          break;
        }
      }
    } catch (err: any) {
      showError(err.message || 'Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (fmt: 'csv' | 'xlsx' | 'pdf') => {
    setExportAnchor(null);
    setIsExporting(true);
    try {
      const params = buildApiParams();
      const blob = await apiService.exportReport({ ...params, format: fmt, reportType: activeTab });
      const url = URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      const orgSlug = organization?.name?.replace(/\s+/g, '-').toLowerCase().slice(0, 20) || 'report';
      a.download = `${orgSlug}-${activeTab}-${filters.startDate}-to-${filters.endDate}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccess(`Report exported as ${fmt.toUpperCase()}`);
    } catch (err: any) {
      showError(err.message || 'Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newTab: ReportTab) => {
    setActiveTab(newTab);
    setHasGenerated(false);
    setSummary(null);
    setGroupedData([]);
    setDateData([]);
    setUtilizationData([]);
    setBudgetData([]);
  };

  // ==================== Invoice ====================
  const handleInvoicePreview = async () => {
    if (!invoiceProjectId) {
      showError('Please select a project');
      return;
    }
    setInvoiceLoading(true);
    try {
      const res = await apiService.generateInvoice({
        projectId: invoiceProjectId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      const data = res.data || res;
      setInvoicePreview(data?.invoice || data);
    } catch (err: any) {
      showError(err.message || 'Failed to generate invoice preview');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleInvoiceDownload = async () => {
    setInvoiceLoading(true);
    try {
      const blob = await apiService.generateInvoicePdf({
        projectId: invoiceProjectId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      const url = URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      const selectedProject = projects.find(p => p.id === invoiceProjectId);
      const projectSlug = selectedProject?.name?.replace(/\s+/g, '-').toLowerCase().slice(0, 30) || 'project';
      a.download = `invoice-${invoicePreview?.invoiceNumber || 'draft'}-${projectSlug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccess('Invoice downloaded');
    } catch (err: any) {
      showError(err.message || 'Failed to download invoice PDF');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // ==================== Render Sections ====================
  const renderSummary = () => {
    if (!summary) return null;
    return (
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Total Hours" value={summary.totalHours.toFixed(1)} color="#7b6df6" suffix="h" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Billable Hours" value={summary.billableHours.toFixed(1)} color="#10b981" suffix="h" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Non-Billable" value={summary.nonBillableHours.toFixed(1)} color="#f59e0b" suffix="h" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Billable %" value={summary.billablePercent.toFixed(0)} color="#3b82f6" suffix="%" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Entries" value={summary.entryCount} color="#f43f5e" />
        </Grid>
      </Grid>
    );
  };

  const renderGroupedChart = () => {
    if (!groupedData.length) return null;
    const chartData = groupedData.slice(0, 10);
    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Hours Distribution
        </Typography>
        <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 44)}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 40, left: 0, bottom: 4 }}>
            <XAxis
              type="number"
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}h`}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: '#f1f5f9', fontSize: 13 }}
              width={150}
              axisLine={false}
              tickLine={false}
            />
            <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(123, 109, 246, 0.06)' }} />
            <Bar dataKey="billableHours" name="Billable" stackId="a" fill="#7b6df6" radius={[0, 0, 0, 0]} maxBarSize={28} />
            <Bar dataKey="nonBillableHours" name="Non-Billable" stackId="a" fill="#334155" radius={[0, 6, 6, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  const renderGroupedTable = () => {
    if (!groupedData.length) return null;
    return (
      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Billable</TableCell>
              <TableCell align="right">Non-Billable</TableCell>
              <TableCell align="right">% of Total</TableCell>
              <TableCell align="right">Entries</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedData.map((row, i) => (
              <TableRow key={row.id || i} sx={{ '&:hover': { bgcolor: 'rgba(123, 109, 246, 0.04)' } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 10, height: 10, borderRadius: '50%',
                      bgcolor: CHART_COLORS[i % CHART_COLORS.length],
                    }} />
                    <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>{row.totalHours.toFixed(1)}h</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{row.billableHours.toFixed(1)}h</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">{row.nonBillableHours.toFixed(1)}h</Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, row.percentOfTotal)}
                      sx={{
                        width: 60, height: 6, borderRadius: 3,
                        bgcolor: 'rgba(148,163,184,0.1)',
                        '& .MuiLinearProgress-bar': { bgcolor: CHART_COLORS[i % CHART_COLORS.length], borderRadius: 3 },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 36 }}>
                      {row.percentOfTotal.toFixed(0)}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">{row.entryCount}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDateChart = () => {
    if (!dateData.length) return null;
    const chartData = dateData.map((d) => ({
      ...d,
      label: format(new Date(d.date + 'T00:00:00'), 'MMM d'),
    }));
    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Daily Hours
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
            <XAxis
              dataKey="label"
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}h`}
            />
            <RechartsTooltip content={<ChartTooltip />} />
            <Line
              type="monotone"
              dataKey="totalHours"
              name="Total"
              stroke="#7b6df6"
              strokeWidth={2.5}
              dot={{ r: 3, fill: '#7b6df6' }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="billableHours"
              name="Billable"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 2.5, fill: '#10b981' }}
              strokeDasharray="4 4"
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  const renderDateTable = () => {
    if (!dateData.length) return null;
    return (
      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Total Hours</TableCell>
              <TableCell align="right">Billable</TableCell>
              <TableCell align="right">Non-Billable</TableCell>
              <TableCell align="right">Entries</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dateData.map((row) => (
              <TableRow key={row.date} sx={{ '&:hover': { bgcolor: 'rgba(123, 109, 246, 0.04)' } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(row.date + 'T00:00:00'), 'EEE, MMM d')}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>{row.totalHours.toFixed(1)}h</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{row.billableHours.toFixed(1)}h</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {row.nonBillableHours.toFixed(1)}h
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">{row.entryCount}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderUtilization = () => {
    if (!utilizationData.length) return null;
    return (
      <>
        {renderGroupedChart()}
        <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Team Member</TableCell>
                <TableCell align="right">Total Hours</TableCell>
                <TableCell align="right">Billable Hours</TableCell>
                <TableCell align="right">Non-Billable</TableCell>
                <TableCell align="right">Utilization %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {utilizationData.map((row, i) => (
                <TableRow key={row.id || i} sx={{ '&:hover': { bgcolor: 'rgba(123, 109, 246, 0.04)' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{row.totalHours.toFixed(1)}h</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.billableHours.toFixed(1)}h</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">{row.nonBillableHours.toFixed(1)}h</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, row.percentOfTotal)}
                        sx={{
                          width: 60, height: 6, borderRadius: 3,
                          bgcolor: 'rgba(148,163,184,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: row.percentOfTotal >= 80 ? '#10b981' : row.percentOfTotal >= 50 ? '#f59e0b' : '#ef4444',
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 36 }}>
                        {row.percentOfTotal.toFixed(0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const renderBudget = () => {
    if (!budgetData.length) return null;
    return (
      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell align="right">Budget</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell align="right">% Used</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {budgetData.map((row: any, i: number) => (
              <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(123, 109, 246, 0.04)' } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{row.budgetHours.toFixed(1)}h</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>{row.actualHours.toFixed(1)}h</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={row.remainingHours < 0 ? 'error.main' : 'text.secondary'}>
                    {row.remainingHours.toFixed(1)}h
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, row.percentUsed)}
                      sx={{
                        width: 60, height: 6, borderRadius: 3,
                        bgcolor: 'rgba(148,163,184,0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: row.percentUsed > 100 ? '#ef4444' : row.percentUsed > 80 ? '#f59e0b' : '#10b981',
                          borderRadius: 3,
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ minWidth: 36, color: row.percentUsed > 100 ? 'error.main' : 'text.primary' }}
                    >
                      {row.percentUsed.toFixed(0)}%
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderEmptyState = () => (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Assessment sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {hasGenerated ? 'No data found' : 'Generate a Report'}
      </Typography>
      <Typography variant="body2" color="text.disabled">
        {hasGenerated
          ? 'Try adjusting your filters or date range.'
          : 'Set your filters and click "Generate Report" to view results.'}
      </Typography>
    </Box>
  );

  const renderResults = () => {
    if (isLoading) return <LoadingSpinner message="Generating report..." />;

    if (!hasGenerated) return renderEmptyState();

    switch (activeTab) {
      case 'summary':
        return summary ? renderSummary() : renderEmptyState();
      case 'by-project':
      case 'by-user':
      case 'by-client':
        return groupedData.length > 0 ? (
          <>
            {renderGroupedChart()}
            {renderGroupedTable()}
          </>
        ) : renderEmptyState();
      case 'by-date':
        return dateData.length > 0 ? (
          <>
            {renderDateChart()}
            {renderDateTable()}
          </>
        ) : renderEmptyState();
      case 'utilization':
        return utilizationData.length > 0 ? renderUtilization() : renderEmptyState();
      case 'budget':
        return budgetData.length > 0 ? renderBudget() : renderEmptyState();
      default:
        return renderEmptyState();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Reports</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Analyze time tracking data across projects, clients, and team members.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasGenerated && (
            <>
              <Button
                variant="outlined"
                startIcon={<InvoiceIcon />}
                onClick={() => setInvoiceOpen(true)}
              >
                Generate Invoice
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                endIcon={<ArrowDropDown />}
                onClick={(e) => setExportAnchor(e.currentTarget)}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => handleExport('csv')}>
                  <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
                  Export CSV
                </MenuItem>
                <MenuItem onClick={() => handleExport('xlsx')}>
                  <ListItemIcon><TableChart fontSize="small" /></ListItemIcon>
                  Export Excel
                </MenuItem>
                <MenuItem onClick={() => handleExport('pdf')}>
                  <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                  Export PDF
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="End Date"
              type="date"
              size="small"
              fullWidth
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={projects}
              getOptionLabel={(o) => o.name}
              value={projects.filter((p) => filters.projectIds.includes(p.id))}
              onChange={(_, vals) => setFilters((f) => ({ ...f, projectIds: vals.map((v) => v.id) }))}
              renderInput={(params) => <TextField {...params} label="Projects" placeholder="All" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{ bgcolor: option.color + '30', borderColor: option.color }}
                    variant="outlined"
                  />
                ))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={clients}
              getOptionLabel={(o) => o.name}
              value={clients.filter((c) => filters.clientIds.includes(c.id))}
              onChange={(_, vals) => setFilters((f) => ({ ...f, clientIds: vals.map((v) => v.id) }))}
              renderInput={(params) => <TextField {...params} label="Clients" placeholder="All" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={members}
              getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
              value={members.filter((m) => filters.userIds.includes(m.id))}
              onChange={(_, vals) => setFilters((f) => ({ ...f, userIds: vals.map((v) => v.id) }))}
              onOpen={loadMembers}
              renderInput={(params) => <TextField {...params} label="Users" placeholder="All" />}
              loading={!membersLoaded}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.billableOnly}
                  onChange={(e) => setFilters((f) => ({ ...f, billableOnly: e.target.checked }))}
                  color="primary"
                />
              }
              label={<Typography variant="body2">Billable Only</Typography>}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const now = new Date();
                setFilters({
                  startDate: format(subDays(now, 6), 'yyyy-MM-dd'),
                  endDate: format(now, 'yyyy-MM-dd'),
                  projectIds: [], clientIds: [], userIds: [],
                  billableOnly: false,
                });
              }}
            >
              Last 7 Days
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const now = new Date();
                setFilters({
                  startDate: format(subDays(now, 29), 'yyyy-MM-dd'),
                  endDate: format(now, 'yyyy-MM-dd'),
                  projectIds: [], clientIds: [], userIds: [],
                  billableOnly: false,
                });
              }}
            >
              Last 30 Days
            </Button>
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={generateReport}
              disabled={isLoading}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            '& .MuiTab-root': { minHeight: 48 },
          }}
        >
          <Tab label="Summary" value="summary" icon={<Assessment sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="By Project" value="by-project" icon={<BarChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="By User" value="by-user" icon={<BarChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="By Client" value="by-client" icon={<BarChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="By Date" value="by-date" icon={<CalendarMonth sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Utilization" value="utilization" icon={<TrendingUp sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Budget" value="budget" icon={<AccountBalance sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Results */}
      {renderResults()}

      {/* Invoice Dialog */}
      <Dialog open={invoiceOpen} onClose={() => { setInvoiceOpen(false); setInvoicePreview(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 2 }}>
            <Autocomplete
              size="small"
              options={projects.filter((p) => p.isBillable)}
              getOptionLabel={(o) => o.name}
              value={projects.find((p) => p.id === invoiceProjectId) || null}
              onChange={(_, val) => { setInvoiceProjectId(val?.id || ''); setInvoicePreview(null); }}
              renderInput={(params) => <TextField {...params} label="Select Project" placeholder="Choose a billable project" />}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              Period: {filters.startDate} to {filters.endDate}
            </Typography>
          </Box>

          {invoicePreview && (
            <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>INVOICE</Typography>
                  <Typography variant="body2" color="text.secondary">
                    #{invoicePreview.invoiceNumber}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">Date: {invoicePreview.date}</Typography>
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">From</Typography>
                  <Typography variant="body2" fontWeight={600}>{invoicePreview.from?.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">To</Typography>
                  <Typography variant="body2" fontWeight={600}>{invoicePreview.to?.name || 'N/A'}</Typography>
                  {invoicePreview.to?.contactName && (
                    <Typography variant="body2">{invoicePreview.to.contactName}</Typography>
                  )}
                  {invoicePreview.to?.contactEmail && (
                    <Typography variant="body2" color="text.secondary">{invoicePreview.to.contactEmail}</Typography>
                  )}
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Project:</strong> {invoicePreview.project}
              </Typography>

              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoicePreview.lineItems?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.date}</TableCell>
                        <TableCell>{item.user}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.hours.toFixed(2)}</TableCell>
                        <TableCell align="right">${item.rate.toFixed(2)}</TableCell>
                        <TableCell align="right">${item.amount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {invoicePreview.totalHours?.toFixed(2)}
                      </TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${invoicePreview.totalAmount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInvoiceOpen(false); setInvoicePreview(null); }}>Cancel</Button>
          {!invoicePreview ? (
            <Button
              variant="contained"
              onClick={handleInvoicePreview}
              disabled={!invoiceProjectId || invoiceLoading}
            >
              {invoiceLoading ? 'Loading...' : 'Preview'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleInvoiceDownload}
              disabled={invoiceLoading}
            >
              {invoiceLoading ? 'Downloading...' : 'Download PDF'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default ReportsPage;
